import React from 'react'
import store from '../../app/store'
import Navigator from '../../app/navigator'
import { IssueLink, IssueTrackerProject } from '../../domain/Error'

interface Props {
    errorGroupId: string
    issues: IssueLink[]
    trackers: IssueTrackerProject[]
    navigator: Navigator
    parameters: { [key: string]: string | number }
}

interface State {
    modalOpen: boolean
    selectedTrackerId: number | null
    issueId: string
    submitting: boolean
    error: string | null
}

export class IssueLinks extends React.Component<Props, State> {
    public state: State = {
        modalOpen: false,
        selectedTrackerId: null,
        issueId: '',
        submitting: false,
        error: null,
    }

    private openModal = () => {
        this.setState({
            modalOpen: true,
            selectedTrackerId: this.props.trackers[0]?.id ?? null,
            issueId: '',
            error: null,
        })
    }

    private closeModal = () => this.setState({ modalOpen: false })

    private reload = () => {
        store.dispatch({ type: 'Errors.purge' })
        this.props.navigator.replace({
            ...this.props.parameters,
            reload: (+this.props.parameters.reload || 0) + 1,
        })
    }

    private submit = (event: React.FormEvent) => {
        event.preventDefault()

        if (!this.state.selectedTrackerId || this.state.issueId.trim().length === 0) {
            this.setState({ error: 'Select a tracker and enter an issue ID.' })
            return
        }

        this.setState({ submitting: true, error: null })

        const url = this.props.navigator.path('XhprofErrorBundle.IssueLinks.create', {
            error: this.props.errorGroupId,
        })

        fetch(url, {
            method: 'POST',
            credentials: 'same-origin',
            headers: {
                Accept: 'application/json',
                'Content-Type': 'application/json',
                'X-Requested-With': 'XMLHttpRequest',
            },
            body: JSON.stringify({
                trackerId: this.state.selectedTrackerId,
                issueId: this.state.issueId.trim(),
            }),
        }).then(response => {
            this.setState({ submitting: false })

            if (!response.ok) {
                response.json().then(
                    data => this.setState({ error: data.msg ?? 'Failed to link issue.' }),
                    () => this.setState({ error: 'Failed to link issue.' }),
                )
                return
            }

            this.setState({ modalOpen: false })
            this.reload()
        })
    }

    private deleteIssue = (issue: IssueLink) => {
        if (!window.confirm('Remove this issue link?')) {
            return
        }

        const url = this.props.navigator.path('XhprofErrorBundle.IssueLinks.delete', {
            error: this.props.errorGroupId,
            issueLink: issue.id,
        })

        fetch(url, {
            method: 'DELETE',
            credentials: 'same-origin',
            headers: {
                Accept: 'application/json',
                'X-Requested-With': 'XMLHttpRequest',
            },
        }).then(() => this.reload())
    }

    public render() {
        const { issues, trackers } = this.props

        if (trackers.length === 0) {
            return null
        }

        return (
            <div className="tw-mb-8" data-testid="issue-links">
                <dl className="dl-horizontal tw-mb-0">
                    <dt>Issue Links</dt>
                    <dd>
                        {issues.length === 0 ? (
                            <span className="text-muted tw-mr-8">None</span>
                        ) : (
                            <ul style={{ listStyle: 'none', padding: 0, margin: 0, display: 'inline' }}>
                                {issues.map(issue => (
                                    <li key={issue.id} style={{ display: 'inline-block', marginRight: 8 }}>
                                        <a href={issue.url} target="_blank" rel="noopener noreferrer">
                                            <i className={issue.tracker.iconClass} /> {issue.tracker.name} #{issue.issueId}
                                        </a>
                                        <button
                                            type="button"
                                            className="btn btn-link btn-xs"
                                            onClick={() => this.deleteIssue(issue)}
                                            aria-label="Unlink issue"
                                            title="Unlink issue"
                                        >
                                            <i className="fa fa-times" />
                                        </button>
                                    </li>
                                ))}
                            </ul>
                        )}
                        <button type="button" className="btn btn-default btn-xs" onClick={this.openModal}>
                            <i className="fa fa-link" /> Link with Issue
                        </button>
                    </dd>
                </dl>

                {this.state.modalOpen ? this.renderModal() : null}
            </div>
        )
    }

    private renderModal() {
        return (
            <div className="modal fade in" role="dialog" style={{ display: 'block', backgroundColor: 'rgba(0,0,0,0.5)' }}>
                <div className="modal-dialog">
                    <form className="modal-content" onSubmit={this.submit}>
                        <div className="modal-header">
                            <button type="button" className="close" onClick={this.closeModal} aria-label="Close">
                                <span aria-hidden="true">&times;</span>
                            </button>
                            <h4 className="modal-title">Link with Issue</h4>
                        </div>
                        <div className="modal-body">
                            {this.state.error ? <div className="alert alert-danger">{this.state.error}</div> : null}

                            <div className="form-group">
                                <label htmlFor="issue-tracker-project">Issue Tracker</label>
                                <select
                                    id="issue-tracker-project"
                                    className="form-control"
                                    value={this.state.selectedTrackerId ?? ''}
                                    onChange={e => this.setState({ selectedTrackerId: Number(e.target.value) })}
                                >
                                    {this.props.trackers.map(tracker => (
                                        <option key={tracker.id} value={tracker.id}>
                                            {tracker.name} ({tracker.productLabel})
                                        </option>
                                    ))}
                                </select>
                            </div>

                            <div className="form-group">
                                <label htmlFor="issue-link-id">Issue ID</label>
                                <input
                                    id="issue-link-id"
                                    type="text"
                                    className="form-control"
                                    placeholder="1234, PROJ-42, or paste an issue URL"
                                    value={this.state.issueId}
                                    onChange={e => this.setState({ issueId: e.target.value })}
                                />
                            </div>
                        </div>
                        <div className="modal-footer">
                            <button type="button" className="btn btn-default" onClick={this.closeModal}>
                                Cancel
                            </button>
                            <button type="submit" className="btn btn-primary" disabled={this.state.submitting}>
                                Link Issue
                            </button>
                        </div>
                    </form>
                </div>
            </div>
        )
    }
}
